import { APIRoutes } from "@/api";
import { Intervals } from "@/constants";
import { channelsSchema } from "@/controllers/channel/schema";
import { queryClient } from "@/lib/query-client";
import { useWsStore } from "@/providers/ws-provider";
import { ApiService } from "@/services/api/api-service";
import {
  InfiniteData,
  QueryFunctionContext,
  useInfiniteQuery as useInfiniteQueryTanstack,
} from "@tanstack/react-query";
import { z } from "zod";

const pageParamSchema = z.object({
  cursor: z.number(),
});

type TPageParams = z.infer<typeof pageParamSchema> | undefined;
type TQueryKey = typeof ChatChannelsQuery.keys.all;

async function getChannels({
  pageParam,
  signal,
}: QueryFunctionContext<TQueryKey, TPageParams>) {
  let url = APIRoutes.USER_CHATS as string;

  const paramsResult = pageParamSchema.safeParse(pageParam);
  if (paramsResult.success) {
    const params = new URLSearchParams({
      cursor: paramsResult.data.cursor.toString(),
    });

    url = `${url}?${params.toString()}`;
  }

  const data = await fetch(url, {
    cache: "no-cache",
    signal,
  }).then((res) => res.json());

  try {
    const response = ApiService.parseApiResponse(data, channelsSchema);

    if (response.status === "error") {
      return [];
    } else {
      return response.data;
    }
  } catch (error) {
    console.error(error);
    return [];
  }
}

export type TData = z.infer<typeof channelsSchema>;

export const ChatChannelsQuery = Object.freeze({
  keys: Object.freeze({
    all: ["channels"] as const,
  }),

  updaters: Object.freeze({
    updateChannel:
      (channel: TData[number]) => (oldData: InfiniteData<TData>) => {
        return {
          pages: oldData.pages.map((page) =>
            page.map((c) => (c.id === channel.id ? channel : c)),
          ),
          pageParams: oldData.pageParams,
        };
      },
    updateLatestMessage:
      (channelId: string, message: TData[number]["messages"][number]) =>
      (oldData: InfiniteData<TData>) => {
        return {
          pages: oldData.pages.map((page) =>
            page.map((c) => {
              if (c.id !== channelId) {
                return c;
              }

              return {
                ...c,
                messages: [message],
              };
            }),
          ),
          pageParams: oldData.pageParams,
        } satisfies InfiniteData<TData>;
      },
  }),

  invalidate() {
    return queryClient.invalidateQueries({
      queryKey: ChatChannelsQuery.keys.all,
    });
  },

  getData() {
    return queryClient.getQueryData<InfiniteData<TData>>(
      ChatChannelsQuery.keys.all,
    );
  },

  updateData(updater: (oldData: InfiniteData<TData>) => InfiniteData<TData>) {
    return queryClient.setQueryData(ChatChannelsQuery.keys.all, updater);
  },

  useInfiniteQuery() {
    const isConnected = useWsStore((state) => state.isConnected);

    return useInfiniteQueryTanstack<
      TData,
      unknown,
      InfiniteData<TData>,
      TQueryKey,
      TPageParams
    >({
      queryKey: ChatChannelsQuery.keys.all,
      queryFn: getChannels,
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (!lastPage || lastPage?.length === 0) return undefined;

        return {
          cursor: lastPage[lastPage.length - 1].createdAt.getTime(),
        };
      },
      refetchInterval: isConnected ? false : Intervals.MINUTE,
    });
  },
});
